import { cancelable } from 'cancelable-promise';
import { debounce } from 'lodash-es';
import { browser } from '../../../../helpers/BrowserHelper';
import { EmbeddedDataMediaPlayOptionsRepeat, EmbeddedDataMediaType } from '../../../../types/EmbeddedData.type';
import { capDimensions, clamp, mapRange } from '../../../../utils/math';
import { millisecondsToSeconds } from '../../../../utils/time';
import { PdfJsHelper } from '../../helpers/pdfjs.helper';
export class EmbeddedMediaLayerComponent {
    constructor(embeddedData, container, pdfjsLib, pdfPage, pdfPageExtractedImages, disableAutoPlay, maxCanvasPixels = 100 * 100) {
        this.embeddedData = embeddedData;
        this.container = container;
        this.pdfjsLib = pdfjsLib;
        this.pdfPage = pdfPage;
        this.pdfPageExtractedImages = pdfPageExtractedImages;
        this.disableAutoPlay = disableAutoPlay;
        this.maxCanvasPixels = maxCanvasPixels;
        this.htmlElement = null;
        this.className = 'sp-embedded-media-layer-component';
        this.mediaRenderers = [];
        this.canvasRenderedAtScale = null;
        this.media = this.orchestrateMedia([...this.embeddedData.media]);
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.container.appendChild(this.htmlElement);
        this.pdfPageDimensions = PdfJsHelper.calculatePageDimensions(this.pdfPage);
    }
    get scale() {
        return this.containerWidth / this.pdfPageDimensions.width;
    }
    get containerWidth() {
        return this.container.clientWidth;
    }
    get containerHeight() {
        return this.container.clientHeight;
    }
    get pdfWidth() {
        return this.pdfPageDimensions.width;
    }
    get pdfHeight() {
        return this.pdfPageDimensions.height;
    }
    getOperatorList() {
        if (this.getOperatorListPromise) {
            return this.getOperatorListPromise;
        }
        this.getOperatorListPromise = cancelable(new Promise((resolve, reject) => {
            this.pdfPage.getOperatorList().then(pdfPageOperatorList => {
                this.pdfPageOperatorList = pdfPageOperatorList;
                resolve();
            }, reject);
        }));
        return this.getOperatorListPromise;
    }
    renderMedia() {
        if (this.mediaRenderers.length) {
            return;
        }
        this.mediaRenderers = [];
        this.media
            .sort((itemA, itemB) => (itemA.renderIndex > itemB.renderIndex ? 1 : -1))
            .forEach((item) => {
            switch (item.type) {
                case EmbeddedDataMediaType.VIDEO:
                case EmbeddedDataMediaType.GIF:
                    const image = this.pdfPageExtractedImages
                        .filter(image => image !== null)
                        .find(image => image.index === item.renderIndex);
                    const posterSrc = image ? image.element.getAttribute('xlink:href') : null;
                    const videoRenderer = new EmbeddedMediaLayerVideoComponent(this.htmlElement, item, posterSrc, this.pdfWidth);
                    this.mediaRenderers.push(videoRenderer);
                    break;
            }
        });
    }
    pauseMedia() {
        this.mediaRenderers.forEach(mediaRenderer => mediaRenderer.pause());
    }
    render() {
        this.destroyGetOperatorListPromise();
        this.destroyRenderTask();
        this.destroyRenderPromise();
        this.canvasRenderedAtScale = this.scale;
        this.renderPromise = cancelable(new Promise((resolve, reject) => {
            const pixelRatio = window.devicePixelRatio;
            const cappedDimensions = capDimensions(this.containerWidth * pixelRatio, this.containerHeight * pixelRatio, this.maxCanvasPixels);
            const scale = cappedDimensions.width / this.pdfWidth;
            const viewport = this.pdfPage.getViewport({ scale: scale });
            const canvas = (this.canvas = document.createElement('canvas'));
            const canvasContext = canvas.getContext('2d', {
                willReadFrequently: true
            });
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            this.getOperatorList().then(() => {
                const renderContext = {
                    canvasContext,
                    viewport,
                    operatorList: this.injectEraseMediaOperations(this.pdfPageOperatorList, this.media.map(item => item.renderIndex))
                };
                this.renderTask = this.pdfPage.render(renderContext);
                this.renderTask.promise
                    .then(() => {
                    this.renderTask = null;
                    this.renderMedia();
                    this.destroyCanvases();
                    this.htmlElement.appendChild(this.canvas);
                    resolve();
                })
                    .catch(error => {
                    if (error.type === 'canvas') {
                        console.log('EmbeddedMediaLayerComponent :: error instanceof RenderingCancelledException');
                        return;
                    }
                    reject();
                });
            });
        }));
        return this.renderPromise;
    }
    resize() {
        this.mediaRenderers.forEach(mediaRenderer => mediaRenderer.resize());
    }
    destroy() {
        this.destroyGetOperatorListPromise();
        this.destroyRenderPromise();
        this.destroyRenderTask();
        this.destroyCanvases();
        this.canvas = null;
        if (this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }
        this.mediaRenderers.forEach(mediaRenderer => mediaRenderer.destroy());
        this.htmlElement.innerHTML = '';
    }
    orchestrateMedia(media) {
        if (this.disableAutoPlay === true) {
            media.map(item => (item.playOptions.autoPlay = false));
            return media;
        }
        // on ios autoplay will only work in certain cases for example if the media is muted
        if (browser.ios) {
            media.map(item => (item.playOptions.autoPlay = false));
            return media;
        }
        // When we have a mixture of autoplay values we set all media to autoplay false
        const autoplayValues = media
            .map(item => item.playOptions.autoPlay)
            .filter((autoPlay, index, items) => items.indexOf(autoPlay) === index);
        if (autoplayValues.length > 1) {
            media.map(item => (item.playOptions.autoPlay = false));
        }
        return media;
    }
    injectEraseMediaOperations(operatorList, mediaRenderIndexes) {
        const imageFns = ['paintImageXObject', 'paintJpegXObject'];
        const OPS = this.pdfjsLib.OPS;
        const REVOPS = [];
        for (const op in OPS) {
            REVOPS[OPS[op]] = op;
        }
        /*
        // BEGIN DEBUG
        const operatorListReadable = operatorList.fnArray.map((fnId, i) => {
            return {
                name: REVOPS[fnId],
                index: i,
                fnId: fnId,
                fn: REVOPS[fnId],
                args: operatorList.argsArray[i]
            };
        });

        console.groupCollapsed(`Operations`);
        console.log(operatorListReadable);
        console.groupEnd();
        console.groupCollapsed(`Image operations`);
        console.log(operatorListReadable.filter(operation => imageFns.indexOf(operation.fn) !== -1));
        console.groupEnd();
        // END DEBUG
        */
        const mediaPaintOperatorIndexes = operatorList.fnArray
            .map((fnId, i) => {
            return {
                name: REVOPS[fnId],
                index: i,
                fnId: fnId,
                fn: REVOPS[fnId],
                args: operatorList.argsArray[i]
            };
        })
            .filter(operation => imageFns.indexOf(operation.fn) !== -1)
            .filter((operation, i) => mediaRenderIndexes.indexOf(i) !== -1)
            .map(operation => operation.index);
        mediaPaintOperatorIndexes.forEach((paintOperatorIndex, i) => {
            paintOperatorIndex += i;
            operatorList.argsArray.splice(paintOperatorIndex, 0, [[['BM', 'destination-out']]]);
            operatorList.fnArray.splice(paintOperatorIndex, 0, OPS.setGState);
        });
        return operatorList;
    }
    destroyCanvases() {
        const elements = this.htmlElement.querySelectorAll('canvas');
        for (let i = 0; i < elements.length; i++) {
            const canvas = elements[i];
            canvas.parentNode.removeChild(canvas);
        }
    }
    destroyRenderPromise() {
        if (this.renderPromise) {
            this.renderPromise.cancel();
            this.renderPromise = null;
        }
    }
    destroyGetOperatorListPromise() {
        if (this.getOperatorListPromise) {
            this.getOperatorListPromise.cancel();
            this.getOperatorListPromise = null;
        }
    }
    destroyRenderTask() {
        if (this.renderTask) {
            this.renderTask.cancel();
            this.renderTask = null;
        }
    }
}
export class EmbeddedMediaLayerVideoComponent {
    constructor(container, media, posterSrc, pdfDefaultWidth) {
        this.container = null;
        this.htmlElement = null;
        this.className = 'sp-embedded-media-layer-video-component';
        this.videoContainerElement = null;
        this.videoElement = null;
        this.imageElement = null;
        this.intersectionElement = null;
        this.container = container;
        this.media = media;
        this.posterSrc = posterSrc;
        this.pdfDefaultWidth = pdfDefaultWidth;
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.videoContainerElement = document.createElement('div');
        this.videoElement = document.createElement('video');
        this.intersectionElement = document.createElement('div');
        this.addVideoEventListeners();
        this.render();
    }
    get scale() {
        return this.containerWidth / this.pdfDefaultWidth;
    }
    get containerWidth() {
        return this.container.clientWidth;
    }
    get containerHeight() {
        return this.container.clientHeight;
    }
    get intersectionRect() {
        const htmlElementRect = this.htmlElement.getBoundingClientRect();
        const videoContainerElementRect = this.videoContainerElement.getBoundingClientRect();
        return this.intersection(htmlElementRect, videoContainerElementRect);
    }
    addVideoEventListeners() {
        this.oneVideoPlay = this.oneVideoPlay.bind(this);
        this.videoElement.addEventListener('play', this.oneVideoPlay);
    }
    removeVideoEventListeners() {
        this.videoElement.removeEventListener('play', this.oneVideoPlay);
    }
    oneVideoPlay() {
        this.load();
        this.videoElement.classList.remove('off');
        if (this.imageElement && this.imageElement.parentNode) {
            this.imageElement.parentNode.removeChild(this.imageElement);
        }
        this.videoElement.removeEventListener('play', this.oneVideoPlay);
    }
    render() {
        const videoElement = this.videoElement;
        videoElement.className = `${this.className}__video`;
        videoElement.controls = false;
        videoElement.width = this.media.size.width;
        videoElement.height = this.media.size.height;
        videoElement.autoplay = this.media.playOptions.autoPlay;
        videoElement.loop = this.media.playOptions.repeat !== EmbeddedDataMediaPlayOptionsRepeat.NONE;
        videoElement.playsInline = true;
        if (this.posterSrc) {
            this.imageElement = document.createElement('img');
            this.imageElement.className = `${this.className}__poster`;
            this.imageElement.src = this.posterSrc;
            this.htmlElement.appendChild(this.imageElement);
        }
        this.container.appendChild(this.htmlElement);
        const trim = this.media.playOptions.trim;
        const trimStart = trim && trim.start ? millisecondsToSeconds(trim.start) : 0;
        const trimEnd = trim && trim.end ? millisecondsToSeconds(trim.end) : 0;
        const maxVolume = mapRange(this.media.playOptions.volume, 0, 100, 0, 1);
        const repeatCount = isNaN(parseInt(this.media.playOptions.repeat)) ? null : parseInt(this.media.playOptions.repeat);
        this.controls = new EmbeddedMediaLayerVideoControlsComponent(this.container, videoElement, trimStart, trimEnd, maxVolume, repeatCount);
        if (videoElement.autoplay) {
            this.load();
        }
        this.videoContainerElement.appendChild(videoElement);
        this.htmlElement.appendChild(this.videoContainerElement);
        this.resize();
    }
    load() {
        if (this.videoElement.src) {
            return;
        }
        this.videoElement.src = this.media.uri.processed || this.media.uri.converted;
    }
    pause() {
        this.videoElement.pause();
    }
    resize() {
        const mediaSize = this.media.size;
        const arrangement = this.media.arrangement;
        const size = arrangement.size;
        const offset = arrangement.offset;
        const crop = arrangement.crop;
        const flip = arrangement.flip;
        const flipVertical = flip.vertical === true ? -1 : 1;
        const flipHorizontal = flip.horizontal === true ? -1 : 1;
        let top = 0;
        let left = 0;
        let height = 100;
        let width = 100;
        if (crop) {
            width =
                (mediaSize.width / (mediaSize.width - mediaSize.width * (crop.left / 100) - mediaSize.width * (crop.right / 100))) * 100;
            height =
                (mediaSize.height / (mediaSize.height - mediaSize.height * (crop.top / 100) - mediaSize.height * (crop.bottom / 100))) *
                    100;
            top = -((height * crop.top) / 100);
            left = -((width * crop.left) / 100);
        }
        this.videoContainerElement.style.cssText = `position: absolute; top:${top}%; left:${left}%; width:${width}%; height:${height}%;`;
        if (this.imageElement) {
            this.htmlElement.style.cssText = `width:${size.width * this.scale}px; height:${size.height *
                this.scale}px; transform: translate(${offset.x * this.scale}px, ${offset.y *
                this.scale}px) rotate(${0}deg) scale(${flipHorizontal}, ${flipVertical})`;
            const intersectionRect = this.intersectionRect;
            const htmlElementRect = this.htmlElement.getBoundingClientRect();
            intersectionRect.top -= htmlElementRect.top;
            intersectionRect.left -= htmlElementRect.left;
            this.imageElement.style.cssText = `position: absolute; top:${intersectionRect.top}px; left:${intersectionRect.left}px; width:${intersectionRect.width}px; height:${intersectionRect.height}px`;
        }
        this.htmlElement.style.cssText = `width:${size.width * this.scale}px; height:${size.height *
            this.scale}px; transform: translate(${offset.x * this.scale}px, ${offset.y * this.scale}px) rotate(${arrangement.rotation}deg) scale(${flipHorizontal}, ${flipVertical})`;
        const scaleX = this.videoContainerElement.clientWidth / mediaSize.width;
        const scaleY = this.videoContainerElement.clientHeight / mediaSize.height;
        this.videoElement.style.cssText = `position: absolute; top:0; left:0; transform: scale(${scaleX}, ${scaleY});`;
        this.controls.resize(this.intersectionRect);
    }
    intersection(a, b) {
        const left = Math.max(a['left'], b['left']);
        const top = Math.max(a['top'], b['top']);
        const right = Math.min(a['right'], b['right']);
        const bottom = Math.min(a['bottom'], b['bottom']);
        const width = right - left;
        const height = bottom - top;
        return {
            left,
            top,
            right,
            bottom,
            width,
            height
        };
    }
    destroy() {
        this.removeVideoEventListeners();
        if (this.controls) {
            this.controls.destroy();
        }
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.src = '';
        }
        if (this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }
        this.htmlElement.innerHTML = '';
    }
}
export class EmbeddedMediaLayerVideoControlsComponent {
    constructor(container, videoElement, trimStart = null, trimEnd = null, maxVolume = 100, repeatCount = null) {
        this.container = null;
        this.htmlElement = null;
        this.className = 'sp-embedded-media-layer-video-controls-component';
        this.videoElement = null;
        this.videoDuration = null;
        this.maxVolume = null; // 0 - 1
        this.repeatCount = null;
        this.startTime = null;
        this.endTime = null;
        this.duration = null;
        this.isLoadingMetaData = true;
        this.isBuffering = true;
        this.isScrubbing = false;
        this.isPlaying = false;
        this.wasPlaying = null;
        this.isMuted = false;
        this.playedCount = 0;
        this.previousCurrentTime = null;
        this.debouncedVideoWaitingMilliseconds = 100;
        this.MINI_BREAKPOINT = 300;
        this.TINY_BREAKPOINT = 150;
        this.container = container;
        this.videoElement = videoElement;
        this.trimStart = trimStart;
        this.trimEnd = trimEnd;
        this.maxVolume = this.videoElement.volume = maxVolume;
        this.repeatCount = repeatCount;
        this.isPlaying = this.videoElement.autoplay;
        this.startTime = trimStart;
        // when video loops waiting event gets triggered which shows buffering for a fraction of a second, so we debounce it
        this.debouncedVideoWaiting = debounce(this.onVideoWaiting.bind(this), this.debouncedVideoWaitingMilliseconds);
        this.render();
        this.update();
        this.addEventListeners();
    }
    get currentTime() {
        return mapRange(this.videoCurrentTime, this.startTime, this.endTime, 0, this.duration);
    }
    get currentTimeFormatted() {
        return this.secondsToMinutesSeconds(this.currentTime);
    }
    get remainingTime() {
        return mapRange(this.videoCurrentTime, this.startTime, this.endTime, this.duration, 0);
    }
    get remainingTimeFormatted() {
        return `-${this.secondsToMinutesSeconds(this.remainingTime)}`;
    }
    get videoCurrentTime() {
        return clamp(this.videoElement.currentTime, this.startTime, this.endTime);
    }
    get classNames() {
        const classNames = [this.className];
        if (this.isLoadingMetaData) {
            classNames.push(`is-loading-metadata`);
        }
        if (this.isBuffering && this.isPlaying) {
            classNames.push(`is-buffering`);
        }
        if (this.width <= this.TINY_BREAKPOINT) {
            classNames.push(`tiny`);
        }
        else if (this.width <= this.MINI_BREAKPOINT) {
            classNames.push(`mini`);
        }
        if (browser.touch) {
            classNames.push(`is-touch`);
        }
        return classNames.join(' ');
    }
    get width() {
        return this.htmlElement.clientWidth;
    }
    render() {
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.classNames;
        this.htmlElement.innerHTML = `
    <div class="click-area" data-action='play'></div>
    <div class="click-area" data-action='pause'></div>
    <button class="play" data-action='play'>
        <svg viewBox="0 0 102 102" xmlns="http://www.w3.org/2000/svg"><g transform="translate(1 1)" fill="none" fill-rule="evenodd"><circle fill="#FFF" cx="50" cy="50" r="50"/><path fill="#000" d="M63.78 50L41.732 64.567V35.433z"/></g></svg>
    </button>
    <div class="buffering" data-action='buffering'>
        <svg class="circular" viewBox="25 25 50 50">
            <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="2" stroke-miterlimit="10"/>
        </svg>
    </div>
    <div class="toolbar">
        <button data-action='play'>
            <svg fill="currentColor" viewBox="0 0 18 18">
                <path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"></path>
            </svg>
        </button>
        <button data-action='pause'>
            <svg fill="currentColor" viewBox="0 0 18 18">
                <path d="M6 1H3c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V2c0-.6-.4-1-1-1zM12 1c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V2c0-.6-.4-1-1-1h-3z"></path>
            </svg>
        </button>
        <div data-id="currentTime" class="time"></div>
        <input data-id="progress" type="range" min="0" max="1" step="0.01" value="0" autocomplete="off" role="slider">
        <div data-id="remainingTime" class="time"></div>
        ${(maxVolume => {
            if (maxVolume === 0) {
                return ``;
            }
            return `
            <button data-action='mute'>
                <svg fill="currentColor" viewBox="0 0 18 18">
                    <path d="M15.6 3.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4C15.4 5.9 16 7.4 16 9c0 1.6-.6 3.1-1.8 4.3-.4.4-.4 1 0 1.4.2.2.5.3.7.3.3 0 .5-.1.7-.3C17.1 13.2 18 11.2 18 9s-.9-4.2-2.4-5.7z"></path><path d="M11.282 5.282a.909.909 0 0 0 0 1.316c.735.735.995 1.458.995 2.402 0 .936-.425 1.917-.995 2.487a.909.909 0 0 0 0 1.316c.145.145.636.262 1.018.156a.725.725 0 0 0 .298-.156C13.773 11.733 14.13 10.16 14.13 9c0-.17-.002-.34-.011-.51-.053-.992-.319-2.005-1.522-3.208a.909.909 0 0 0-1.316 0zM3.786 6.008H.714C.286 6.008 0 6.31 0 6.76v4.512c0 .452.286.752.714.752h3.072l4.071 3.858c.5.3 1.143 0 1.143-.602V2.752c0-.601-.643-.977-1.143-.601L3.786 6.008z"></path>
                </svg>
            </button>
            <button data-action='unmute'>
                <svg fill="currentColor" viewBox="0 0 18 18">
                    <path d="M12.4 12.5l2.1-2.1 2.1 2.1 1.4-1.4L15.9 9 18 6.9l-1.4-1.4-2.1 2.1-2.1-2.1L11 6.9 13.1 9 11 11.1zM3.786 6.008H.714C.286 6.008 0 6.31 0 6.76v4.512c0 .452.286.752.714.752h3.072l4.071 3.858c.5.3 1.143 0 1.143-.602V2.752c0-.601-.643-.977-1.143-.601L3.786 6.008z"></path>
                </svg>
            </button>`;
        })(this.maxVolume)}
        <button data-action="enterFullscreen">
            <svg fill="currentColor" viewBox="0 0 18 18"><path d="M10 3h3.6l-4 4L11 8.4l4-4V8h2V1h-7zM7 9.6l-4 4V10H1v7h7v-2H4.4l4-4z"></path></svg>
        </button>
        <button data-action="exitFullscreen">
            <svg fill="currentColor" viewBox="0 0 18 18"><path fill="currentColor" d="M1 12h3.6l-4 4L2 17.4l4-4V17h2v-7H1zM16 .6l-4 4V1h-2v7h7V6h-3.6l4-4z"></path></svg>
        </button>
    </div>`;
        this.progressElement = this.htmlElement.querySelector(`[data-id='progress']`);
        this.currentTimeElement = this.htmlElement.querySelector(`[data-id='currentTime']`);
        this.remainingTimeElement = this.htmlElement.querySelector(`[data-id='remainingTime']`);
        this.container.appendChild(this.htmlElement);
    }
    update() {
        this.htmlElement.className = this.classNames;
        const actions = ['play', 'pause', 'mute', 'unmute', 'enterFullscreen', 'exitFullscreen'];
        const currentActions = [this.isPlaying ? 'pause' : 'play', this.isMuted ? 'unmute' : 'mute'];
        actions.forEach(action => {
            const elements = this.htmlElement.querySelectorAll(`[data-action='${action}']`);
            for (let i = 0; i < elements.length; i++) {
                const element = elements[i];
                currentActions.indexOf(action) === -1 ? element.classList.add('off') : element.classList.remove('off');
            }
        });
    }
    updateVideoDuration(duration) {
        this.videoDuration = duration;
        this.endTime = this.videoDuration - this.trimEnd;
        this.duration = this.endTime - this.startTime;
        this.progressElement.setAttribute('min', String(this.startTime));
        this.progressElement.setAttribute('max', String(this.endTime));
    }
    addEventListeners() {
        this.onClick = this.onClick.bind(this);
        this.onVideoPlay = this.onVideoPlay.bind(this);
        this.onVideoPause = this.onVideoPause.bind(this);
        this.onVideoWaiting = this.onVideoWaiting.bind(this);
        this.onVideoPlaying = this.onVideoPlaying.bind(this);
        this.onVideoTimeUpdate = this.onVideoTimeUpdate.bind(this);
        this.onVideoLoadedMetaData = this.onVideoLoadedMetaData.bind(this);
        this.htmlElement.addEventListener('click', this.onClick);
        this.onProgressInput = this.onProgressInput.bind(this);
        this.onProgressChange = this.onProgressChange.bind(this);
        this.videoElement.addEventListener('play', this.onVideoPlay);
        this.videoElement.addEventListener('pause', this.onVideoPause);
        this.videoElement.addEventListener('waiting', this.debouncedVideoWaiting);
        this.videoElement.addEventListener('playing', this.onVideoPlaying);
        this.videoElement.addEventListener('timeupdate', this.onVideoTimeUpdate);
        this.videoElement.addEventListener('loadedmetadata', this.onVideoLoadedMetaData);
        this.progressElement.addEventListener('input', this.onProgressInput);
        this.progressElement.addEventListener('change', this.onProgressChange);
    }
    removeEventListeners() {
        this.htmlElement.removeEventListener('click', this.onClick);
        this.videoElement.removeEventListener('play', this.onVideoPlay);
        this.videoElement.removeEventListener('pause', this.onVideoPause);
        this.videoElement.removeEventListener('waiting', this.onVideoWaiting);
        this.videoElement.removeEventListener('playing', this.onVideoPlaying);
        this.videoElement.removeEventListener('timeupdate', this.onVideoTimeUpdate);
        this.videoElement.removeEventListener('loadedmetadata', this.onVideoLoadedMetaData);
        this.progressElement.removeEventListener('input', this.onProgressInput);
        this.progressElement.removeEventListener('change', this.onProgressChange);
    }
    onClick(e) {
        const target = e.target;
        const action = target.getAttribute('data-action');
        switch (action) {
            case 'play':
                this.videoElement.play();
                break;
            case 'pause':
                this.videoElement.pause();
                break;
            case 'mute':
                this.isMuted = true;
                this.videoElement.volume = 0;
                break;
            case 'unmute':
                this.isMuted = false;
                this.videoElement.volume = this.maxVolume;
                break;
        }
        this.update();
    }
    onVideoLoadedMetaData() {
        this.isLoadingMetaData = false;
        this.videoElement.currentTime = this.startTime;
        this.updateVideoDuration(this.videoElement.duration);
        /*
        console.log(
            `videoDuration: ${this.videoDuration} trimStart: ${this.trimStart} trimEnd: ${this.trimEnd} startTime: ${
                this.startTime
            } endTime: ${this.endTime} duration: ${this.duration} repeatCount: ${this.repeatCount}`
        );
        */
        this.update();
    }
    onVideoTimeUpdate() {
        this.debouncedVideoWaiting.cancel();
        this.isBuffering = false;
        this.progressElement.value = String(this.videoCurrentTime);
        this.currentTimeElement.innerHTML = this.currentTimeFormatted;
        this.remainingTimeElement.innerHTML = this.remainingTimeFormatted;
        // When using hls.js the duration can change on the fly as streams can change
        if (this.videoDuration !== this.videoElement.duration) {
            this.updateVideoDuration(this.videoElement.duration);
        }
        // Repeat logic
        if (this.repeatCount !== null && this.isScrubbing === false && this.isPlaying) {
            if (this.videoElement.currentTime < this.previousCurrentTime) {
                this.playedCount++;
                if (this.playedCount === this.repeatCount) {
                    this.videoElement.pause();
                    this.playedCount = 0;
                }
            }
        }
        if (this.isScrubbing === false && this.isPlaying) {
            // If video is dynamically trimmed we need to watch for loop and played manually 😞
            if (this.trimStart > 0 || this.trimEnd > 0) {
                if (this.videoElement.loop && Math.floor(this.videoElement.currentTime) >= Math.floor(this.endTime)) {
                    this.videoElement.currentTime = this.startTime;
                }
                else if (this.videoElement.currentTime >= this.endTime) {
                    this.videoElement.pause();
                    this.videoElement.currentTime = this.endTime;
                }
            }
        }
        this.previousCurrentTime = this.videoElement.currentTime;
        this.update();
    }
    onVideoPlay(e) {
        this.isPlaying = true;
        if (Math.floor(this.videoElement.currentTime) >= Math.floor(this.endTime)) {
            this.videoElement.currentTime = this.startTime;
        }
        this.update();
    }
    onVideoPause(e) {
        this.isPlaying = false;
        this.update();
    }
    onVideoWaiting(e) {
        this.isBuffering = true;
        this.update();
    }
    onVideoPlaying(e) {
        this.debouncedVideoWaiting.cancel();
        this.isBuffering = false;
        this.update();
    }
    onProgressInput(e) {
        if (this.isScrubbing === false) {
            this.isScrubbing = true;
            this.videoElement.pause();
            this.wasPlaying = this.isPlaying;
        }
        const target = e.target;
        this.videoElement.currentTime = parseFloat(target.value);
    }
    onProgressChange(e) {
        this.isScrubbing = false;
        if (this.wasPlaying) {
            this.videoElement.play();
        }
        this.wasPlaying = null;
    }
    secondsToMinutesSeconds(seconds) {
        return Math.floor(seconds / 60) + ':' + ('0' + Math.floor(seconds % 60)).slice(-2);
    }
    resize(videoClientRect) {
        const videoRect = this.fromClientRect(videoClientRect);
        const containerRect = this.fromClientRect(this.container.getBoundingClientRect());
        const intersectionRect = this.intersection(containerRect, videoRect);
        intersectionRect.x1 -= containerRect.x1;
        intersectionRect.y1 -= containerRect.y1;
        intersectionRect.x2 -= containerRect.x1;
        intersectionRect.y2 -= containerRect.y1;
        this.htmlElement.style.cssText = `position: absolute; top: ${intersectionRect.y1}px; left: ${intersectionRect.x1}px; width: ${intersectionRect.x2 - intersectionRect.x1}px; height: ${intersectionRect.y2 - intersectionRect.y1}px;`;
        this.update();
    }
    intersection(a, b) {
        return {
            x1: Math.max(a.x1, b.x1),
            y1: Math.max(a.y1, b.y1),
            x2: Math.min(a.x2, b.x2),
            y2: Math.min(a.y2, b.y2)
        };
    }
    fromClientRect(clientRect) {
        return {
            x1: clientRect.left,
            y1: clientRect.top,
            x2: clientRect.right,
            y2: clientRect.bottom
        };
    }
    destroy() {
        this.removeEventListeners();
        if (this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }
        this.htmlElement.innerHTML = '';
    }
}
//# sourceMappingURL=embedded-media-layer.component.js.map